import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Calendar, Pill, CheckCircle2, Sparkles, Video, MapPin, Plus, AlertTriangle } from "lucide-react";
import api, { drName } from "../lib/api";
import { useAuth } from "../lib/auth";
import { FullScreenLoader } from "../components/ui-kit";
import SOSButton from "../components/SOSButton";

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  } catch { return iso; }
};

const Dashboard = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/patient/dashboard").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <FullScreenLoader />;
  const d = data || {};
  const upcoming = d.upcoming_appointments || [];
  const rx = d.active_prescription;
  const todos = d.todays_todos || [];
  const anomalies = d.anomalies || [];
  const quote = d.quote || "The greatest wealth is health.";

  return (
    <div className="pb-28 relative">
      {/* Header */}
      <div className="px-5 pt-8 pb-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-[#78716C]">Hi, {user?.name?.split(" ")[0] || "there"} 👋</p>
            <h1 className="font-instrument text-3xl font-semibold text-[#0A4C36] leading-tight mt-0.5">Your health today</h1>
          </div>
          <Link to="/profile" data-testid="dashboard-avatar"
            className="w-11 h-11 rounded-2xl bg-[#0A4C36] text-white font-instrument text-lg font-semibold flex items-center justify-center">
            {(user?.name || "P").slice(0, 1).toUpperCase()}
          </Link>
        </div>
      </div>

      {/* Quote card */}
      <div className="px-5 mb-5">
        <div className="rounded-3xl p-5 bg-gradient-to-br from-[#F4844B] to-[#E8672A] text-white relative overflow-hidden">
          <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute right-10 bottom-2 w-16 h-16 rounded-full bg-white/10" />
          <Sparkles size={18} className="opacity-80 mb-2" />
          <p className="font-instrument text-lg leading-snug relative z-10" data-testid="dashboard-quote">&ldquo;{quote}&rdquo;</p>
        </div>
      </div>

      {/* Upcoming appointments */}
      <div className="mb-6">
        <div className="px-5 flex items-center justify-between mb-3">
          <h2 className="font-instrument text-lg font-semibold text-[#1C1917]">Upcoming</h2>
          <Link to="/appointments" data-testid="dashboard-view-appointments" className="text-sm text-[#0A4C36] font-medium">All →</Link>
        </div>
        {upcoming.length === 0 ? (
          <div className="px-5">
            <button data-testid="dashboard-book-empty" onClick={() => nav("/book")}
              className="w-full card flex items-center justify-between text-left border-dashed border-2 border-[#F4844B]/30 bg-[#FFF7EE]">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-[#FCD9C8] text-[#F4844B] flex items-center justify-center">
                  <Plus size={22} />
                </div>
                <div>
                  <div className="font-medium text-[#1C1917]">Book your first appointment</div>
                  <div className="text-xs text-[#78716C] mt-0.5">Choose from top doctors near you</div>
                </div>
              </div>
              <ChevronRight size={18} className="text-[#78716C]" />
            </button>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto hide-scrollbar px-5 snap-x snap-mandatory">
            {upcoming.map((a) => (
              <div key={a.id} data-testid={`appt-card-${a.id}`}
                className="min-w-[280px] card snap-start">
                <div className="flex items-center justify-between mb-3">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E6F0EC] text-[#0A4C36] text-xs font-medium">
                    {a.mode === "Remote" ? <Video size={12} /> : <MapPin size={12} />}
                    {a.mode}
                  </div>
                  <div className="text-xs text-[#78716C]">{fmtDate(a.date)} · {a.time}</div>
                </div>
                <div className="font-instrument text-lg font-medium text-[#1C1917]">{drName(a.doctor_name)}</div>
                <div className="text-xs text-[#78716C] mt-0.5">{Array.isArray(a.specialties) ? a.specialties.join(", ") : (a.specialty || "")}</div>
                {a.mode === "Remote" && a.meeting_url && (
                  <Link to={`/meeting?url=${encodeURIComponent(a.meeting_url)}`} data-testid={`join-meeting-${a.id}`}
                    className="mt-4 inline-block btn-primary text-center text-sm py-2.5">
                    Join meeting
                  </Link>
                )}
              </div>
            ))}
            <button data-testid="dashboard-book-new" onClick={() => nav("/book")}
              className="min-w-[100px] snap-start rounded-3xl border-2 border-dashed border-[#F4844B]/40 bg-[#FFF7EE] flex flex-col items-center justify-center gap-1 text-[#F4844B]">
              <Plus size={22} />
              <span className="text-xs font-medium">Book</span>
            </button>
          </div>
        )}
      </div>

      {/* Anomalies alert */}
      {anomalies.length > 0 && (
        <div className="px-5 mb-5">
          <div className="rounded-3xl p-4 bg-[#FEF2F2] border border-red-200 flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-red-100 text-red-500 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-instrument font-semibold text-[#1C1917]">Vitals need attention</div>
              <div className="text-xs text-[#57534E] mt-0.5">
                {anomalies.length} anomaly {anomalies.length > 1 ? "readings" : "reading"} detected recently
              </div>
              <Link to="/vitals" data-testid="dashboard-view-anomalies" className="inline-block mt-3 text-sm font-medium text-red-500">
                Review vitals →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Active prescription */}
      {rx && (
        <div className="px-5 mb-5">
          <h2 className="font-instrument text-lg font-semibold text-[#1C1917] mb-3">Active prescription</h2>
          <div className="card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-2xl bg-[#E6F0EC] text-[#0A4C36] flex items-center justify-center">
                <Pill size={20} />
              </div>
              <div>
                <div className="font-medium text-[#1C1917]">{drName(rx.doctor_name)}</div>
                <div className="text-xs text-[#78716C]">{fmtDate(rx.created_at || rx.date)}</div>
              </div>
            </div>
            {(rx.medications || []).slice(0, 3).map((m, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-t border-[#0A4C36]/5">
                <div>
                  <div className="text-sm font-medium text-[#1C1917]">{m.name}</div>
                  <div className="text-xs text-[#78716C]">{m.dosage} · {m.frequency}</div>
                </div>
                <span className="text-xs text-[#78716C]">{m.duration}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Today's todos */}
      {todos.length > 0 && (
        <div className="px-5 mb-5">
          <h2 className="font-instrument text-lg font-semibold text-[#1C1917] mb-3">Today&rsquo;s activities</h2>
          <div className="card space-y-2.5">
            {todos.map((t, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-[#F4844B] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#1C1917] leading-snug">{typeof t === "string" ? t : t.text || t.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="px-5 mt-6">
        <button data-testid="dashboard-book-cta" onClick={() => nav("/book")} className="btn-primary flex items-center justify-center gap-2">
          <Calendar size={18} /> Book new appointment
        </button>
      </div>

      <SOSButton />
    </div>
  );
};

export default Dashboard;
